import type { WaitlistFieldErrors } from './schema';

/**
 * The browser's only way to the waitlist: the same-origin routes under
 * `/api/waitlist`. The manage session travels in `x-waitlist-session` and
 * lives in React state only — no cookie, no storage — so a reload ends it and
 * the page asks for a fresh link.
 */

export type BffResult<T> =
  | { ok: true; status: number; data: T }
  | {
      ok: false;
      status: number;
      code: string;
      error: string;
      fields?: WaitlistFieldErrors;
      retryAfter?: number;
    };

export const OFFLINE_MESSAGE = 'Check your connection and try again.';

export async function bff<T>(
  path: string,
  opts: { method?: 'GET' | 'POST' | 'PATCH'; body?: unknown; session?: string } = {},
): Promise<BffResult<T>> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.session) headers['x-waitlist-session'] = opts.session;
  let res: Response;
  try {
    res = await fetch(`/api/waitlist${path}`, {
      method: opts.method ?? 'POST',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      cache: 'no-store',
      credentials: 'omit',
    });
  } catch {
    return { ok: false, status: 0, code: 'network', error: OFFLINE_MESSAGE };
  }
  const body = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    data?: T;
    code?: string;
    error?: string;
    fields?: WaitlistFieldErrors;
    retryAfter?: number;
  };
  // Success only on a 2xx that says so and carries data.
  if (res.ok && body.ok === true && body.data !== undefined) return { ok: true, status: res.status, data: body.data };
  return {
    ok: false,
    status: res.status,
    code: body.code ?? (res.ok ? 'unavailable' : 'unknown'),
    error: body.error ?? "We couldn't complete that just now. Please try again in a few minutes.",
    fields: body.fields,
    retryAfter: body.retryAfter,
  };
}
